// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/ResearchCard.tsx
// ADAPTED (plan K0/K1): TP's markup and states, driven only by the snapshot and the brand guide. TP's scripted
// narration, dwell times, answer gates, fast-forward and findings fixtures (Reddit, reviews) are not ported.
import { AlertCircle, ChevronDown, Filter, Gauge, Globe, Megaphone, Search, type LucideIcon } from 'lucide-react';
import { Fragment, useId, useState, type ReactNode } from 'react';

import { BrandLogo } from '@/components/ui/brand-logo';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card } from '@/components/ui/shadcn/card';
import { Spinner } from '@/components/ui/spinner';
import { StepCheck } from '@/components/ui/step-check';
import type { BrandGuideResponse, SessionSnapshot } from '@/contracts/onboarding-session';
import { cn } from '@/lib/utils';

import { COULDNT_READ, countLabel, RESEARCH_STEPS } from '../copy';
import { RESEARCH_VISIBLE_TAIL } from '../constants';
import { researchRow, type PartState } from '../state/buildTranscript';
import { researchClock, researchPhase } from '../state/researchState';
import { prospectLogoUrl, researchSteps, terminalStepCount, visibleSteps, type TimelineStep } from '../state/researchSteps';
import { Elapsed } from './Elapsed';
import { Dot, ResearchFindings } from './ResearchFindings';
import { STEP_STATUS_TEXT, StatusToken, StepStatusIcon } from './StepStatusIcon';

/** The icon tile of a row, by the kind of job it reports (the id prefix: `siteProfile`, `adAnalysis:meta`, a CI stage). */
function iconOf(id: string): LucideIcon {
  if (id === 'siteProfile') return Globe;
  if (id.startsWith('adDiscovery')) return Megaphone;
  if (id.startsWith('adAnalysis')) return Gauge;
  if (id === 'funnel') return Filter;
  return Search;
}

function Part({ state, children, what }: { state: PartState; children?: ReactNode; what: string }) {
  if (state === 'loading') {
    return (
      <span className="inline-flex items-center">
        <Spinner size="sm" />
        <span className="sr-only">{`Reading ${what}`}</span>
      </span>
    );
  }
  if (state === 'unavailable') return <span>{`${COULDNT_READ} ${what}`}</span>;
  if (state === 'absent') return null;
  return <span className="text-foreground">{children}</span>;
}

export interface ResearchCardProps {
  snapshot: SessionSnapshot | null;
  /** The brand guide read so far (null until it loads): the "Brand profile" and "Best sellers" findings come from it. */
  brandGuide?: BrandGuideResponse | null;
  /** The site as typed, for the title before the first snapshot; the snapshot's own bare host wins once it is there. */
  site?: string;
}

/**
 * TP's research card, on real job states. One block that updates in place: the header says where the research
 * stands (spinner, check, or "Partly read" when it ended with gaps), the line under it carries the per-part values
 * (name, category, products, live ads), the newest steps show with their state (the full list one click away), and
 * what the research found opens as a row (brand profile, best sellers) as it lands and folds once the research is
 * done. The card is not a live region: the chat's log announces it, a refresh never does.
 */
export function ResearchCard({ snapshot, brandGuide = null, site: siteProp }: ResearchCardProps) {
  const [expanded, setExpanded] = useState(false);
  const stepsId = useId();
  const site = snapshot?.domain || siteProp || 'your site';
  const phase = researchPhase(snapshot);
  const settled = phase.kind !== 'running';
  const steps = researchSteps(snapshot, brandGuide);
  // Collapsed: the newest rows plus any running one that sits earlier, so work in progress is never behind the link.
  const tail = expanded ? { shown: steps, hidden: 0 } : visibleSteps(steps, RESEARCH_VISIBLE_TAIL);
  const hidden = tail.hidden;
  const shown = settled && !expanded ? [] : tail.shown;
  const terminal = terminalStepCount(steps);
  const clock = researchClock(snapshot);
  const row = researchRow(snapshot, brandGuide);
  // A part whose job the BFF never started is left out: neither a spinner nor "Couldn't read" for work that is not happening.
  const summary = [
    { key: 'name', state: row.name.state, what: 'the name', value: <b className="font-semibold">{row.name.value}</b> },
    { key: 'category', state: row.category.state, what: 'the category', value: row.category.value },
    {
      key: 'products',
      state: row.products.state,
      what: 'the products',
      value: row.products.value !== null ? countLabel(row.products.value, 'product', 'products') : null,
    },
    {
      key: 'liveAds',
      state: row.liveAds.state,
      what: 'the ads',
      value: row.liveAds.value !== null ? countLabel(row.liveAds.value, 'live Meta ad', 'live Meta ads') : null,
    },
  ].filter((part) => part.state !== 'absent');
  const brand = snapshot?.research.name ?? brandGuide?.name.value ?? site;
  const logo = prospectLogoUrl(brandGuide);

  return (
    <Card role="region" aria-label="Site research" data-testid="research-card" data-state={phase.kind} className="w-full overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={stepsId}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-ds-ink-a05 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-focus"
      >
        <BrandLogo name={brand} src={logo} className="size-6" />
        <span className={cn('min-w-0 flex-1 truncate text-heading-sm text-foreground', !settled && 'onb-shimmer')}>
          {settled ? `Researched ${site}` : `Researching ${site}`}
        </span>
        {phase.kind === 'running' ? <Spinner size="sm" /> : null}
        {phase.kind === 'done' ? (
          <span role="img" aria-label="Research done" className="flex">
            <StepCheck />
          </span>
        ) : null}
        {phase.kind === 'gaps' ? (
          <>
            <StatusToken tone={phase.tone}>
              <AlertCircle className="size-3.5" />
            </StatusToken>
            <Badge intent={phase.tone} className="shrink-0">
              Partly read
            </Badge>
          </>
        ) : null}
        <span className="shrink-0 text-label tabular-nums text-ds-text-2">
          {RESEARCH_STEPS.stepsProgress(terminal, steps.length)}
          {clock.startedAt !== null ? (
            // The clock is decoration for the eye: it must not become part of the toggle's accessible name (it ticks).
            <span aria-hidden="true">
              {' · '}
              <Elapsed startedAt={clock.startedAt} frozenAt={clock.frozenAt} />
            </span>
          ) : null}
        </span>
        <ChevronDown aria-hidden className={cn('size-4 shrink-0 text-ds-text-2 transition-transform', expanded && 'rotate-180')} />
      </button>

      <p
        data-testid="research-summary"
        className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 px-3 pb-2 pl-[38px] text-label text-ds-text-2"
      >
        {summary.map((part, i) => (
          <Fragment key={part.key}>
            {i > 0 ? <Dot /> : null}
            <Part state={part.state} what={part.what}>
              {part.value}
            </Part>
          </Fragment>
        ))}
      </p>

      {/* Always in the DOM, so the toggle's aria-controls resolves; `hidden` takes it out of view and the tree. */}
      <div id={stepsId} hidden={shown.length === 0}>
        <ol className="flex flex-col gap-1.5 px-3 pb-3 pl-[38px]" aria-label="Research steps">
          {hidden > 0 ? (
            <li>
              <Button type="button" variant="link" size="xs" className="h-auto px-0" onClick={() => setExpanded(true)}>
                {hidden} earlier {hidden === 1 ? 'step' : 'steps'}
              </Button>
            </li>
          ) : null}
          {shown.map((s) => (
            <StepRow key={s.id} step={s} showChildren={expanded} />
          ))}
        </ol>
      </div>

      <ResearchFindings brandGuide={brandGuide} brand={brand} site={site} done={settled} />
    </Card>
  );
}

function StepRow({ step, showChildren }: { step: TimelineStep; showChildren: boolean }) {
  const Icon = iconOf(step.id);
  const children = showChildren ? (step.children ?? []) : [];
  return (
    <li
      data-status={step.status}
      data-step-id={step.id}
      className="-ml-[26px] flex flex-col gap-1.5 animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none"
    >
      <div className="flex items-center gap-2.5">
        <span aria-hidden className="flex size-6 shrink-0 items-center justify-center rounded-sm bg-ds-ink-a05 text-ds-text-2">
          <Icon className="size-3.5" />
        </span>
        <StepLabel step={step} className="text-body-sm" />
        <StepStatusIcon status={step.status} />
        <span className="sr-only">{`(${STEP_STATUS_TEXT[step.status]})`}</span>
      </div>
      {children.length > 0 ? (
        // CI's stages, under the icon column: smaller rows with the same status icon.
        <ol aria-label={`${step.label} stages`} className="flex flex-col gap-1 pl-[34px]">
          {children.map((child) => (
            <li key={child.id} data-status={child.status} data-step-id={child.id} className="flex items-center gap-2.5">
              <StepLabel step={child} className="text-label" />
              <StepStatusIcon status={child.status} />
              <span className="sr-only">{`(${STEP_STATUS_TEXT[child.status]})`}</span>
            </li>
          ))}
        </ol>
      ) : null}
    </li>
  );
}

/** A row's label and its notes. A failed step says so in words as well as in its icon, after whatever note it carries (never twice). */
function StepLabel({ step, className }: { step: TimelineStep; className: string }) {
  const notes = [step.detail, step.status === 'unavailable' && step.detail !== COULDNT_READ ? COULDNT_READ : undefined].filter(Boolean);
  return (
    <span className={cn('min-w-0 flex-1 truncate', className)}>
      <span className={step.status === 'running' ? 'onb-shimmer' : step.status === 'queued' ? 'text-ds-text-2' : 'text-foreground'}>
        {step.label}
      </span>
      {notes.map((note) => (
        <span key={note} className="ml-1.5 text-label text-ds-text-3">
          {note}
        </span>
      ))}
    </span>
  );
}
